import { Component, Vue } from 'vue-property-decorator';

import { ConsentService } from '@/services/identity-service';
import { ConsentDetails } from '@/interfaces/consent/consent-details';
import { ConsentType } from '@/constants/consent-type';
import { OpenBankingConsent } from '@/interfaces/consent/open-banking-consent';
import { convertAmountToCents } from '@/utilities/amount';

const AccountConsent = () => import('./account-consent');
const PaymentConsent = () => import('./payment-consent');

@Component({
    data() {
        return {
            loading: true,
            canceling: false,
            processing: false,
            consentInfo: null
        }
    }
})
export default class Consent extends Vue {
    loading: boolean;
    canceling: boolean;
    processing: boolean;
    returnUrl: string;
    clientName: string;
    consentInfo: ConsentDetails;
    eventId: string | undefined;

    async mounted() {
        this.loading = true;

        const response = await ConsentService.getConsentDetails(this.$route.query.returnUrl as string ?? '');

        if (!response.success) {
            // Generic error, or 404

            return;
        }

        const { returnUrl, clientName, consentInfo } = response.data;

        this.returnUrl = returnUrl;
        this.clientName = clientName;
        this.consentInfo = consentInfo;
        this.eventId = response.eventId;

        this.loading = false;
    }

    get consentType(): ConsentType {
        if (!this.consentInfo) {
            return ConsentType.None;
        }

        if (this.consentInfo?.ais) {
            return ConsentType.Account;
        }

        if (this.consentInfo?.pis) {
            return ConsentType.Payment;
        }

        return ConsentType.None;
    }

    get consentTypeComponent() {
        switch (this.consentType) {
            case ConsentType.Account:
                return AccountConsent;

            case ConsentType.Payment:
                return PaymentConsent;

            default:
                return null;
        }
    }

    setOpenBankingConsentOptions(deny: boolean = false): OpenBankingConsent {
        return {
            Deny: deny,
            Amount: convertAmountToCents(this.consentInfo.pis?.payment?.instructedAmount?.amount),
            WalletId: this.consentInfo.pis?.payment?.debtorAccount?.bban,
            Currency: this.consentInfo.pis?.payment?.instructedAmount?.currency,
            ReturnUrl: this.returnUrl,
            PaymentId: this.consentInfo.pis?.paymentId,
            ConsentId: this.consentInfo.ais?.consentId,
            CreditorName: this.consentInfo.pis?.payment?.creditorName,
            CreditorIban: this.consentInfo.pis?.payment?.creditorAccount?.iban,
            RemittanceInfoUnstructured: this.consentInfo.pis?.payment?.remittanceInfoUnstructured
        };
    }

    async cancel() {
        this.canceling = true;

        const data = this.setOpenBankingConsentOptions(true);

        const response = await ConsentService.submitConsent(data);

        if (!response.success) {
            // ToDo: Show generic error message
            this.canceling = false;

            return;
        }

        window.location.href = this.consentInfo.ais?.tppRedirectUrl ||
            this.consentInfo.pis?.tppRedirectUrl;
    }

    async proceed(walletId?: string | null) {
        this.processing = true;

        let data = this.setOpenBankingConsentOptions();

        if (walletId) {
            data = {
                ...data,
                WalletId: walletId
            };
        }

        const response = await ConsentService.submitConsent(data);

        if (!response.success) {
            // ToDo: Show generic error message
            this.processing = false;

            return;
        }

        window.location.href = this.returnUrl;
    }
}
